import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import router from './router'
import { isNavigationFailure, NavigationFailureType } from 'vue-router'
import App from './App.vue'
import { isChunkLoadError, decideChunkRecovery, clearChunkGuard, pageLoadFailure } from './lib/chunkRecovery'
import '../css/app.css'

// Self-healing reload, but loop-safe: a given cause may trigger at most ONE
// reload per tab session. A stale cache or a failed chunk can therefore no
// longer put the app into an infinite reload storm (it heals once, then stops).
function reloadOnce(key) {
    try {
        if (sessionStorage.getItem(key)) return
        sessionStorage.setItem(key, '1')
    } catch (_) { /* private mode / storage blocked: allow a single reload */ }
    window.location.reload()
}

// A dynamically-imported route chunk failed to load (usually a stale index/cache
// after a deploy). Reload once to pick up fresh assets.
//
// This cannot rescue a failure of THIS file. On 2026-09-07 a deploy left Cloudflare
// holding a cached 404 for the entry bundle itself — cf-cache-status HIT with
// x-do-orig-status 404, cached during the window where the new container was up but
// public/build was not yet written. Nothing below ever executed, so every page rendered
// white and no handler here could react. Only purging the Cloudflare cache, or shipping
// a build whose asset hashes differ, clears it. Worth a Cloudflare rule that refuses to
// cache non-2xx for /build/*, since that window exists on every deploy.
window.addEventListener('vite:preloadError', () => reloadOnce('reload:preload'))

const app = createApp(App)

if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_APP_ENV ?? 'production',
        integrations: [
            Sentry.browserTracingIntegration({ router }),
            Sentry.replayIntegration(),
        ],
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        tracePropagationTargets: [/\/api\//],
    })
}

app.use(createPinia())
app.use(router)

// A lazy route chunk failed to import (usually a stale index/cache after a deploy,
// or a chunk that no longer exists). vite:preloadError does not catch a router
// import() rejection, so handle it here: hard-navigate to the intended route once
// to pull fresh assets. Loop-safe via a per-target sessionStorage guard; once that
// is spent, say so (PageLoadFailed) rather than leave the tab blank.
router.onError((error, to) => {
    if (!isChunkLoadError(error)) return
    const path = to?.fullPath || window.location.pathname
    if (decideChunkRecovery(sessionStorage, path) === 'reload') {
        window.location.assign(to?.fullPath || window.location.href)
        return
    }
    pageLoadFailure.value = { path }
})

router.afterEach((to, _from, failure) => {
    if (failure) return
    clearChunkGuard(sessionStorage, to.fullPath)
    pageLoadFailure.value = null
})

app.mount('#app')

// Dismiss the shell's loader once the FIRST navigation has resolved — not on mount. The
// router renders nothing while a guard is awaiting, and a readable URL resolves its
// workspace and project there, so hiding it at mount would just restore the white screen.
//
// Faded rather than cut, per the design handoff. The node is removed after the transition
// so it cannot sit over the app if the transitionend never fires.
function dismissSplash() {
    const splash = document.getElementById('app-splash')
    if (!splash) return

    splash.classList.add('is-leaving')
    setTimeout(() => splash.remove(), 300)
}

// Not `.finally()`: that re-raises the rejection, and the first navigation is REJECTED on
// purpose whenever a URL points into another company — the router guard sets the company
// and calls window.location.replace, then returns false so nothing renders in the wrong
// tenancy (switchCompanyAndReload). vue-router reports that as NAVIGATION_ABORTED, an
// Error with no message in production, and uncaught it showed as a bare "Error" in the
// console on every cross-company link and every first load after one.
//
// An abort means the reload is already under way, so the splash stays up rather than
// flashing the app shell for a moment. Anything else is a real failure: take the splash
// down so nobody is left staring at it, and let the error surface.
router.isReady().then(dismissSplash, (err) => {
    if (isNavigationFailure(err, NavigationFailureType.aborted)) return
    dismissSplash()
    throw err
})

// Spec 114: PWA service worker (installable + resilient asset caching).
if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch(() => {})
    })
    navigator.serviceWorker.addEventListener('controllerchange', () => reloadOnce('reload:sw'))
    // A desktop pop-up was clicked (sw.js): go to what it was about, inside the app, so nothing
    // typed is lost. Only an address on this site.
    navigator.serviceWorker.addEventListener('message', (e) => {
        const url = e.data?.type === 'cortex:open' ? e.data.url : null
        if (typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')) router.push(url).catch(() => {})
    })
}
